/* In-app auth session, replacing @ai21/platform-auth-sdk. The SDK is
 * unmaintained (last published 2024-11; its source repo's master no longer
 * contains the code that built it) and blocked product-level needs like the
 * per-product email branding — while everything this app used from it boils
 * down to: Firebase login → POST /identity/sign-in (session cookie) →
 * GET /identity/user. That protocol lives in identitySession.ts/firebase.ts;
 * this provider owns the state machine.
 *
 * Mounted only when AUTH_ENABLED (see AuthProvider.tsx) — auth-off
 * environments never initialize Firebase or call the identity service.
 *
 * Invitation deeplinks: invite emails land with /auth?invitation_id=…. For
 * LOGIN and social auth the invite is NOT attached to the exchange — a
 * signed-in viewer is forwarded to the dedicated /invite/:id accept page
 * (LoginRoute), which accepts it explicitly; keeping it out avoids racing
 * Firebase's getRedirectResult, which strips the query on gated loads. The one
 * exception is email SIGN-UP: signupWithEmail threads invitation_id into
 * /sign-in so the brand-new account joins the invited org directly instead of
 * JIT-creating a throwaway one.
 *
 * Social login is a full-page redirect (signInWithRedirect), so it completes
 * across two page loads: the click navigates away to the provider, and the
 * return load lands back here. Bootstrap therefore tries consumeRedirectResult
 * first — if this load is the tail of a redirect it carries the Firebase token,
 * which we run through the same cookie exchange as email login. With no pending
 * redirect it falls back to the ordinary cookie restore. A redirect that comes
 * back but fails the exchange degrades to anonymous and flags socialError so
 * /login can explain the failed attempt. */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { logger } from "../services/telemetry";
import {
  completeAuthAttempt,
  failAuthAttempt,
  flushAuthTelemetry,
  hasPendingAuthAttempt,
  markAuthProviderSucceeded,
  reconcileAbandonedAttempt,
} from "./authAttempt";
import {
  trackLoginFailed,
  trackLoginSucceeded,
  trackSignupSucceeded,
  type AuthMethod,
} from "../analytics/tracking";
import {
  consumeRedirectResult,
  deleteCurrentFirebaseUser,
  firebaseEmailLogin,
  firebaseEmailSignup,
  firebaseSocialLogin,
  sendPasswordReset as firebaseSendPasswordReset,
} from "./firebase";
import {
  fetchSessionUser,
  identitySignIn,
  resendEmailVerification,
  type AuthUser,
} from "./identitySession";
import { redirectIfEmailNotAllowed } from "./authErrors";
import { markWelcomeIntent } from "./postAuthIntent";

export type AuthStatus =
  | "loading"
  | "authenticated"
  // Signed in (Firebase + identity cookie) but the email isn't verified yet —
  // the gate routes these users to the check-email screen, not the dashboard.
  | "unverified"
  | "anonymous";

export interface AuthMethods {
  loginWithEmail: (email: string, password: string) => Promise<void>;
  /** Create an email/password account. `invitationId` (from an invite
   *  deeplink) joins the invited org directly instead of creating a new one.
   *  Returns the new session user so the caller can identify it for analytics
   *  before the (unverified) status flip routes away from the sign-up screen. */
  signupWithEmail: (
    email: string,
    password: string,
    invitationId?: string,
  ) => Promise<AuthUser>;
  /** Google sign-in via full-page redirect (the only social provider). */
  socialLogin: () => Promise<void>;
  /** Clear a stale returning-redirect failure flag (e.g. when the user starts
   *  an email sign-in so the social-error banner doesn't linger). */
  clearSocialError: () => void;
  /** Send a Firebase password-reset email (Firebase hosts the reset page). */
  sendPasswordReset: (email: string) => Promise<void>;
  /** (Re)send the verification email for the current unverified session. */
  resendVerification: () => Promise<void>;
}

/** Map an identity user to an auth status. `emailVerified === false` ⇒
 *  unverified (the gate routes to /check-email); social users are always
 *  verified. A MISSING field is treated as verified — studio parity
 *  (createUserFromIdentityUser defaults undefined→true) — but is logged: the
 *  live backend always returns a strict boolean, so its absence is an
 *  unexpected fail-open worth surfacing. */
function verifiedStatus(user: AuthUser): AuthStatus {
  if (user.emailVerified === false) return "unverified";

  if (user.emailVerified === undefined) {
    logger.warn(
      new Error("identity /user returned no emailVerified field"),
      "verified gate fell open — treating session as authenticated",
    );
  }

  return "authenticated";
}

interface AuthSessionValue {
  status: AuthStatus;
  user: AuthUser | null;
  methods: AuthMethods;
  // True when a returning social-login redirect was picked up but its cookie
  // exchange failed — the user is anonymous and /login should say the last
  // attempt failed. Cleared by a fresh login attempt.
  socialError: boolean;
}

const AuthSessionContext = createContext<AuthSessionValue | null>(null);

export function AuthSessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);
  const [socialError, setSocialError] = useState(false);

  /** Shared tail of both login shapes: exchange the Firebase ID token for the
   *  session cookie and load the session user. Returns the user WITHOUT
   *  touching state, so the caller decides when/whether to commit it (the
   *  bootstrap path applies it only while still mounted — see `active`). */
  const completeLogin = useCallback(
    async (firebaseToken: string, invitationId?: string): Promise<AuthUser> => {
      await identitySignIn(firebaseToken, invitationId);
      const signedIn = await fetchSessionUser();
      if (!signedIn) {
        throw new Error("Signed in, but the session user could not be loaded.");
      }

      return signedIn;
    },
    [],
  );

  /** Commit a completed login to state; the verified gate (studio parity, see
   *  identitySession.ts) decides authenticated vs. unverified. */
  const applySession = useCallback((signedIn: AuthUser) => {
    setUser(signedIn);
    setStatus(verifiedStatus(signedIn));
  }, []);

  const applyLogin = useCallback(
    (signedIn: AuthUser, authMethod: AuthMethod, isNewUser = false) => {
      if (isNewUser) markWelcomeIntent();

      applySession(signedIn);
      if (verifiedStatus(signedIn) !== "authenticated") return;

      if (isNewUser) trackSignupSucceeded(authMethod);
      else trackLoginSucceeded(authMethod, "LOGIN");
    },
    [applySession],
  );

  useEffect(() => {
    let active = true;

    // Bootstrap: a returning social-login redirect completes the login here;
    // otherwise restore any existing session from the cookie. Both end in a
    // single status flip so the gate only ever sees loading → settled.
    void (async () => {
      try {
        const redirect = await consumeRedirectResult();
        if (redirect) {
          // The provider leg is done; the identity exchange still has to land
          // before the attempt counts as complete.
          markAuthProviderSucceeded();
          const signedIn = await completeLogin(redirect.token);
          // "GOOGLE": social is Google-only today. Add a 2nd provider →
          // stash the provider in sessionStorage before signInWithRedirect
          // and read it back here (the URL doesn't carry it).
          if (active) applyLogin(signedIn, "GOOGLE", redirect.isNewUser);

          completeAuthAttempt();

          return;
        }
      } catch (err) {
        /* Both reports go before the branch below, which navigates away via
         * window.location — anything after it never runs, which is how
         * allowlist rejections came to be absent from the funnel entirely.
         * failAuthAttempt closes the AuthStarted (AuthProviderFailed);
         * trackLoginFailed is the separate, pre-existing UserLoginFailed. */
        failAuthAttempt(err);
        trackLoginFailed("GOOGLE", err);

        // Allowlist rejection is terminal — helper redirects via window.location (we're outside the router here).
        if (redirectIfEmailNotAllowed(err)) return;

        // The redirect came back but the exchange failed — surface it on
        // /login rather than silently dropping the user to a blank sign-in.
        logger.error(
          err instanceof Error ? err : new Error(String(err)),
          "Social sign-in redirect failed",
        );
        if (active) {
          setSocialError(true);
          setStatus("anonymous");
        }

        return;
      }

      /* Nothing resolved a redirect above, so any attempt still in flight came
       * back from the provider with no credential — an embedded-webview refusal
       * or an abandoned consent screen. A no-op when no attempt is pending, so
       * an ordinary reload of a signed-in session is untouched. */
      reconcileAbandonedAttempt();

      try {
        const restored = await fetchSessionUser();
        if (!active) return;

        if (restored) applySession(restored);
        else setStatus("anonymous");
      } catch (err) {
        // A restore failure (network, identity down) means "not signed in",
        // not a crash — the gate sends the user to /login.
        logger.warn(
          err instanceof Error ? err : new Error(String(err)),
          "Session restore failed",
        );
        if (active) setStatus("anonymous");
      }
    })();

    return () => {
      active = false;
    };
  }, [completeLogin, applySession, applyLogin]);

  /* Back out of Google's consent screen and the browser restores this document
   * from the back/forward cache: the page comes back FROZEN — React state
   * intact, no reload, so the bootstrap effect above never re-runs. The sign-in
   * button is still spinning from before the redirect and the attempt is never
   * resolved, which reads as "Google never finished". (Reported from a real
   * test; `pageshow.persisted` is the only signal that distinguishes a bfcache
   * restore from a normal load.)
   *
   * Reloading is the recovery: the fresh bootstrap runs consumeRedirectResult
   * (empty), reconciles the attempt into AuthProviderFailed, and rebuilds the
   * screen without the stuck spinner. Gated on an attempt actually being in
   * flight so ordinary back-navigation into the app is untouched — and the
   * reload clears that attempt, so it cannot loop. */
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted || !hasPendingAuthAttempt()) return;

      window.location.reload();
    };

    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const methods = useMemo<AuthMethods>(
    () => ({
      loginWithEmail: async (email, password) => {
        const token = await firebaseEmailLogin(email, password);
        markAuthProviderSucceeded();
        const signedIn = await completeLogin(token);
        applyLogin(signedIn, "EMAIL_PASSWORD");
        completeAuthAttempt();
      },
      // Sign-up creates the Firebase account, then runs the same cookie
      // exchange. The new account is unverified, so the gate shows the
      // check-email screen. If the identity exchange fails AFTER the Firebase
      // account was created, roll the account back so a retry isn't blocked by
      // "email already in use". The backend's invite path skips the auto
      // verification email, so for an invited sign-up we (re)send it.
      signupWithEmail: async (email, password, invitationId) => {
        const token = await firebaseEmailSignup(email, password);
        markAuthProviderSucceeded();
        let signedIn: AuthUser;
        try {
          signedIn = await completeLogin(token, invitationId);
        } catch (err) {
          // Best-effort rollback of the orphaned Firebase account.
          await deleteCurrentFirebaseUser().catch((cleanupErr) => {
            logger.warn(
              cleanupErr instanceof Error
                ? cleanupErr
                : new Error(String(cleanupErr)),
              "Failed to roll back Firebase account after sign-up failure",
            );
          });
          throw err;
        }
        applySession(signedIn);
        completeAuthAttempt();
        if (invitationId) {
          try {
            await resendEmailVerification();
          } catch (err) {
            logger.warn(
              err instanceof Error ? err : new Error(String(err)),
              "Invited sign-up verification resend failed",
            );
          }
        }

        return signedIn;
      },
      // Social login navigates away (signInWithRedirect) and resolves just
      // before that navigation — there's no token to exchange here; the return
      // load picks it up in bootstrap above. Clear any prior failure flag so a
      // fresh attempt starts clean.
      socialLogin: async () => {
        setSocialError(false);
        /* Push AuthStarted (and its departure log) out before the document goes
         * away. Both pipelines batch, so without this the attempt is only
         * reported if the user comes BACK — and the users worth measuring are
         * precisely the ones who do not. Bounded, so it cannot stall the
         * button. */
        await flushAuthTelemetry();
        await firebaseSocialLogin();
      },
      clearSocialError: () => setSocialError(false),
      // Forgot-password is pure Firebase — the identity service has no reset
      // endpoint; Firebase sends the email and hosts the reset page.
      sendPasswordReset: (email) => firebaseSendPasswordReset(email),
      resendVerification: () => resendEmailVerification(),
    }),
    [completeLogin, applySession, applyLogin],
  );

  const value = useMemo(
    () => ({ status, user, methods, socialError }),
    [status, user, methods, socialError],
  );

  return (
    <AuthSessionContext.Provider value={value}>
      {children}
    </AuthSessionContext.Provider>
  );
}

function useAuthSession(): AuthSessionValue {
  const ctx = useContext(AuthSessionContext);
  if (!ctx) {
    throw new Error(
      "Auth session hooks require AuthSessionProvider (auth must be enabled)",
    );
  }

  return ctx;
}

export function useAuthStatus(): { status: AuthStatus } {
  return { status: useAuthSession().status };
}

/** True when a returning social-login redirect failed its cookie exchange, so
 *  the user is back on /login and should be told the last attempt failed. */
export function useSocialAuthError(): boolean {
  return useAuthSession().socialError;
}

export function useAuthUser(): AuthUser | null {
  return useAuthSession().user;
}

export function useAuthMethods(): AuthMethods {
  return useAuthSession().methods;
}

export type { AuthUser };
